import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { createPortal } from "react-dom";
import { create } from "zustand";
import type { MessageAttachment } from "@shared/api";
import { fileSize } from "@shared/lib/format";
import { downloadFile, fileObjectUrl, mediaObjectUrl } from "@shared/ui/FileImage";
import { MenuOn, type MenuItem } from "@shared/ui/Menu";
import { Spinner } from "@shared/ui/spinner";
import { IconChevronLeft, IconChevronRight, IconClose, IconDown, IconExternal, IconZoomIn, IconZoomOut } from "@shared/ui/icons";

/** Что показывает просмотрщик: картинки и видео одного сообщения, по порядку. */
export type MediaItem = Pick<
  MessageAttachment,
  "id" | "filename" | "content_type" | "size" | "width" | "height" | "description"
>;

interface ViewerState {
  items: MediaItem[];
  index: number;
  open: (items: MediaItem[], index: number) => void;
  close: () => void;
  go: (step: number) => void;
}

/**
 * Просмотрщик один на всё приложение — как в Discord: картинку или видео из
 * ленты открывают на весь экран, листают стрелками, закрывают Esc.
 */
export const useMediaViewer = create<ViewerState>((set) => ({
  items: [],
  index: 0,
  open: (items, index) => set({ items, index }),
  close: () => set({ items: [], index: 0 }),
  go: (step) =>
    set((state) =>
      state.items.length === 0 ? state : { index: (state.index + step + state.items.length) % state.items.length },
    ),
}));

/** Можно ли открыть в просмотрщике: картинка или видео, которые сервер разрешил показывать. */
export function isViewable(attachment: Pick<MessageAttachment, "content_type" | "inline">): boolean {
  return attachment.inline && (attachment.content_type.startsWith("image/") || attachment.content_type.startsWith("video/"));
}

const ZOOM_MAX = 6;

/** Ставится один раз у корня приложения. */
export function MediaViewerHost() {
  const { items, index, close, go } = useMediaViewer();
  const item = items[index] ?? null;

  const root = useRef<HTMLDivElement>(null);
  const open = !!item;

  useEffect(() => {
    if (!item) return;
    const onKey = (event: KeyboardEvent) => {
      // Открыто меню картинки — клавиши его: Esc закрывает меню, а не просмотрщик.
      if ((event.target as HTMLElement | null)?.closest?.("[role=menu]")) return;
      // Стрелки и Esc — просмотрщика целиком: под ним звонок листал бы трансляции.
      if (event.key === "Escape") close();
      else if (event.key === "ArrowLeft") {
        if (items.length > 1) go(-1);
      } else if (event.key === "ArrowRight") {
        if (items.length > 1) go(1);
      } else return;
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [item, items.length, close, go]);

  /*
   * Фокус — в просмотрщик, на выходе — туда, где был: иначе набор и Enter
   * уходили в поле ввода под ним, и Enter отправлял сообщение.
   */
  useEffect(() => {
    if (!open) return;
    const before = document.activeElement as HTMLElement | null;
    root.current?.focus();
    return () => before?.focus?.();
  }, [open]);

  if (!item) return null;

  return createPortal(
    <div
      ref={root}
      tabIndex={-1}
      className="media-viewer"
      role="dialog"
      aria-modal
      aria-label={item.filename}
      onPointerDown={(event) => {
        // Мимо картинки — закрыть, как в Discord.
        if (event.target === event.currentTarget) close();
      }}
    >
      <header className="media-viewer-bar">
        <span className="grow ellipsis">
          <span className="media-viewer-name">{item.filename}</span>
          <span className="media-viewer-meta">
            {fileSize(item.size)}
            {item.width && item.height ? ` · ${item.width}×${item.height}` : ""}
            {items.length > 1 ? ` · ${index + 1} из ${items.length}` : ""}
          </span>
        </span>
        <button
          type="button"
          className="media-viewer-btn"
          title="Открыть оригинал"
          aria-label="Открыть оригинал"
          onClick={() => void openOriginal(item)}
        >
          <IconExternal size={18} />
        </button>
        <button
          type="button"
          className="media-viewer-btn"
          title="Скачать"
          aria-label="Скачать"
          onClick={() => void downloadFile(item.id, item.filename)}
        >
          <IconDown size={18} />
        </button>
        <button type="button" className="media-viewer-btn" title="Закрыть (Esc)" aria-label="Закрыть" onClick={close}>
          <IconClose size={18} />
        </button>
      </header>

      {items.length > 1 && (
        <>
          <button
            type="button"
            className="media-viewer-nav prev"
            aria-label="Предыдущее"
            onClick={() => go(-1)}
          >
            <IconChevronLeft size={22} />
          </button>
          <button
            type="button"
            className="media-viewer-nav next"
            aria-label="Следующее"
            onClick={() => go(1)}
          >
            <IconChevronRight size={22} />
          </button>
        </>
      )}

      {item.content_type.startsWith("video/") ? (
        <ViewerVideo key={item.id} item={item} />
      ) : (
        <ViewerImage key={item.id} item={item} onClose={close} />
      )}

      {item.description && <div className="media-viewer-description">{item.description}</div>}

      {items.length > 1 && (
        <div className="media-viewer-strip">
          {items.map((entry, at) => (
            <button
              key={entry.id}
              type="button"
              className={`media-viewer-dot ${at === index ? "on" : ""}`}
              aria-label={`${at + 1}: ${entry.filename}`}
              onClick={() => useMediaViewer.setState({ index: at })}
            />
          ))}
        </div>
      )}
    </div>,
    document.body,
  );
}

/**
 * Оригинал — в новом окне: ссылку на объект открыть можно, адрес с токеном —
 * нет. Окно открывается сразу, по нажатию, а адрес ему дают, когда файл
 * скачан: иначе после долгого ожидания браузер счёл бы окно непрошеным.
 * Ссылку на видео отзываем через минуту — окну её хватит, а держать 25 МБ незачем.
 */
async function openOriginal(item: MediaItem) {
  const tab = window.open("about:blank", "_blank");
  const video = item.content_type.startsWith("video/");
  const url = video ? await mediaObjectUrl(item.id) : await fileObjectUrl(item.id);
  if (tab) tab.location.href = url;
  else window.open(url, "_blank");
  if (video) window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * «Копировать изображение» — как в Discord. Буфер браузера берёт из картинок
 * только PNG, поэтому JPEG, WebP и GIF (первым кадром) перерисовываются в него.
 * Обещание уходит в буфер сразу, а не после перерисовки: запись должна начаться,
 * пока щелчок по пункту ещё считается жестом человека.
 */
async function copyImage(src: string) {
  const png = fetch(src)
    .then((response) => response.blob())
    .then(async (blob) => {
      if (blob.type === "image/png") return blob;
      const bitmap = await createImageBitmap(blob);
      const canvas = document.createElement("canvas");
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      canvas.getContext("2d")?.drawImage(bitmap, 0, 0);
      bitmap.close();
      return new Promise<Blob>((resolve, reject) =>
        canvas.toBlob((out) => (out ? resolve(out) : reject(new Error("PNG не собрался"))), "image/png"),
      );
    });
  await navigator.clipboard.write([new ClipboardItem({ "image/png": png })]);
}

/**
 * Картинка: вписана в окно, щелчок приближает вдвое к точке щелчка и
 * возвращает обратно, колесо — плавно, приближенную тянут мышью.
 */
function ViewerImage({ item, onClose }: { item: MediaItem; onClose: () => void }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [shift, setShift] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; moved: boolean } | null>(null);

  useEffect(() => {
    let alive = true;
    fileObjectUrl(item.id).then(
      (url) => alive && setSrc(url),
      () => alive && setFailed(true),
    );
    return () => {
      alive = false;
    };
  }, [item.id]);

  function zoomTo(next: number, at?: { x: number; y: number }) {
    const clamped = Math.min(ZOOM_MAX, Math.max(1, next));
    if (clamped === 1) {
      setShift({ x: 0, y: 0 });
    } else if (at) {
      // Точка под курсором остаётся на месте.
      const ratio = clamped / zoom;
      setShift((was) => ({ x: at.x - (at.x - was.x) * ratio, y: at.y - (at.y - was.y) * ratio }));
    }
    setZoom(clamped);
  }

  function center(event: { clientX: number; clientY: number }) {
    return { x: event.clientX - window.innerWidth / 2, y: event.clientY - window.innerHeight / 2 };
  }

  if (failed) return <div className="media-viewer-stage muted">Не загрузилось</div>;
  if (!src) {
    return (
      <div className="media-viewer-stage">
        <Spinner />
      </div>
    );
  }

  // Правой кнопкой по картинке — как в Discord.
  const menu: MenuItem[] = [
    { label: "Копировать изображение", onPick: () => void copyImage(src).catch(() => {}) },
    { label: "Сохранить изображение", onPick: () => void downloadFile(item.id, item.filename) },
    { label: "Открыть оригинал", onPick: () => void openOriginal(item) },
  ];

  return (
    <div
      className="media-viewer-stage"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      onWheel={(event) => zoomTo(zoom * (event.deltaY < 0 ? 1.2 : 1 / 1.2), center(event))}
    >
      <MenuOn title={item.filename} items={menu} menuClassName="media-viewer-menu">
        <img
          src={src}
          alt={item.description || item.filename}
          className={`media-viewer-image ${zoom > 1 ? "zoomed" : ""}`}
          draggable={false}
          style={{ transform: `translate(${shift.x}px, ${shift.y}px) scale(${zoom})` }}
          onPointerDown={(event: ReactPointerEvent<HTMLImageElement>) => {
            // Правая кнопка — меню, а не приближение.
            if (event.button !== 0) return;
            drag.current = { x: event.clientX, y: event.clientY, moved: false };
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            const start = drag.current;
            if (!start || zoom === 1) return;
            const dx = event.clientX - start.x;
            const dy = event.clientY - start.y;
            if (Math.abs(dx) + Math.abs(dy) > 3) start.moved = true;
            drag.current = { x: event.clientX, y: event.clientY, moved: start.moved };
            setShift((was) => ({ x: was.x + dx, y: was.y + dy }));
          }}
          onPointerUp={(event) => {
            const start = drag.current;
            drag.current = null;
            if (!start || start.moved) return;
            zoomTo(zoom > 1 ? 1 : 2, center(event));
          }}
        />
      </MenuOn>
      <div className="media-viewer-zoom">
        <button type="button" className="media-viewer-btn" aria-label="Отдалить" onClick={() => zoomTo(zoom / 1.5)}>
          <IconZoomOut size={16} />
        </button>
        <span className="media-viewer-zoom-value">{Math.round(zoom * 100)}%</span>
        <button type="button" className="media-viewer-btn" aria-label="Приблизить" onClick={() => zoomTo(zoom * 1.5)}>
          <IconZoomIn size={16} />
        </button>
      </div>
    </div>
  );
}

/** Видео — со своими кнопками браузера: пауза, громкость, полный экран, картинка в картинке. */
function ViewerVideo({ item }: { item: MediaItem }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    let url: string | null = null;
    mediaObjectUrl(item.id).then(
      (loaded) => {
        url = loaded;
        if (alive) setSrc(loaded);
        else URL.revokeObjectURL(loaded);
      },
      () => alive && setFailed(true),
    );
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [item.id]);

  return (
    <div className="media-viewer-stage">
      {failed ? (
        <span className="muted">Не загрузилось</span>
      ) : src ? (
        <video className="media-viewer-video" src={src} controls autoPlay playsInline />
      ) : (
        <Spinner />
      )}
    </div>
  );
}
